import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';

/**
 * USAePay hosted PayJS card entry for the patient portal Balance page.
 *
 * Mirrors the booking flow (ScheduleConfirm + Schedule.confirm): the
 * PayJS SDK renders card fields into an iframe inside
 * `#portalPaymentCardContainer`, and `tokenize()` exchanges the entered
 * card for a single-use `paymentKey` via `client.getPaymentKey()`. The
 * PAN never crosses our wire — only the resulting key is sent to the
 * backend. The SDK script is loaded once in App.tsx (window.usaepay).
 *
 * PayJS tracks the most-recently-rendered card entry globally, so — like
 * Schedule.confirm — `tokenize()` creates a fresh Client/PaymentCardEntry
 * and still reads the rendered iframe's inputs.
 */
export interface UsaepayCardEntryRef {
  tokenize: () => Promise<{ paymentKey: string } | null>;
}

interface Props {
  epayPublicKey: string;
  onError?: (message: string) => void;
}

const CONTAINER_ID = 'portalPaymentCardContainer';

// Same field layout/styles the booking flow uses (ScheduleConfirm).
const paymentCardConfig = {
  styles: `
    #payjs-container {
      display: grid;
      grid-template-columns: repeat(12, 1fr);
      padding: 0 !important;
      gap: 6px;
    }
    #payjs-input-icon {
      display: none;
    }
    #payjs-cnum-wrapper {
      grid-column: 1 / 12;
      width: 100%;
      padding: 0 !important;
    }
    #payjs-exp-wrapper {
      grid-column: 1 / 6;
      width: 100%;
      padding: 0 !important;
    }
    #payjs-cvv-wrapper {
      grid-column: 7 / 12;
      width: 100%;
      padding: 0 !important;
    }
    .payjs-container input {
      border: 1px solid !important;
      border-color: #d2d5db !important;
      border-radius: 12px !important;
      height: 36px;
      padding-left: 10px;
    }
    @media screen and (max-width: 400px) {
      .payjs-base {
        font-size: 12px;
      }
    }
  `,
};

const UsaepayCardEntry = forwardRef<UsaepayCardEntryRef, Props>(
  ({ epayPublicKey, onError }, ref) => {
    const clientRef = useRef<any>(null);
    const paymentCardRef = useRef<any>(null);

    useEffect(() => {
      const usaepay = (window as any).usaepay;

      if (!usaepay || !epayPublicKey) {
        onError?.('Unable to load the payment form. Please contact the clinic to pay.');

        return;
      }

      const client = new usaepay.Client(epayPublicKey);
      const paymentCard = client.createPaymentCardEntry();
      clientRef.current = client;
      paymentCardRef.current = paymentCard;

      paymentCard.generateHTML(paymentCardConfig);
      paymentCard.addHTML(CONTAINER_ID);
      // Same error contract as ScheduleConfirm: the SDK relays a JSON
      // string; code '0' means "cleared".
      paymentCard.addEventListener('error', (err: any) => {
        try {
          const parsed = JSON.parse(err);

          if (parsed?.code && parsed.code !== '0') {
            onError?.(parsed.message);
          }
        } catch {
          /* non-JSON payloads are ignored, matching the booking flow */
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [epayPublicKey]);

    useImperativeHandle(ref, () => ({
      tokenize: async () => {
        const usaepay = (window as any).usaepay;

        if (!usaepay || !epayPublicKey) {
          onError?.('Unable to load the payment form. Please contact the clinic to pay.');

          return null;
        }

        try {
          // Mirror Schedule.confirm: fresh Client/PaymentCardEntry reads
          // the rendered iframe's inputs.
          const client = new usaepay.Client(epayPublicKey);
          const paymentCard = client.createPaymentCardEntry();
          const result = await client.getPaymentKey(paymentCard);

          if (result?.error) {
            onError?.(result.error);

            return null;
          }

          if (!result?.key) {
            onError?.('Please check your card details and try again.');

            return null;
          }

          return { paymentKey: result.key };
        } catch (err: any) {
          onError?.(err?.message || 'Could not validate that card. Please try again.');

          return null;
        }
      },
    }));

    return <div id={CONTAINER_ID} className="h-24" />;
  }
);

UsaepayCardEntry.displayName = 'UsaepayCardEntry';

export default UsaepayCardEntry;
